import { normalizeQuantity } from './quantity';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumTaxRate = (taxes) =>
  (taxes || []).reduce((sum, tax) => sum + toNumber(tax?.rate), 0);

export const computeTaxAdjustedTotals = ({
  products = [],
  quantities = {},
  discounts = {},
  taxes = [],
  excludedTaxIds = [],
  orderDiscountAmount,
  orderDiscountPercentage,
  isPriceAllowed,
}) => {
  const allowPrice =
    typeof isPriceAllowed === 'function' ? isPriceAllowed : () => true;
  const activeTaxes = (taxes || []).filter(
    (tax) => tax && tax.isActive !== false && tax.branchActive !== false
  );
  const taxesById = new Map(
    activeTaxes.map((tax) => [Number(tax.id), tax])
  );
  const defaultTaxIds = activeTaxes
    .filter((tax) => tax.applyToAllProducts)
    .map((tax) => Number(tax.id));
  const excludedSet = new Set(
    (excludedTaxIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  );

  let subtotal = 0;

  (products || []).forEach((product) => {
    const useDefaultTaxes = product?.useDefaultTaxes !== false;
    let taxIds = useDefaultTaxes ? defaultTaxIds : (product?.taxIds || []);
    taxIds = (taxIds || [])
      .map((id) => Number(id))
      .filter((id) => taxesById.has(id));
    if (!useDefaultTaxes && taxIds.length === 0) {
      taxIds = defaultTaxIds;
    }
    const fullTaxes = taxIds.map((id) => taxesById.get(id)).filter(Boolean);
    const appliedTaxes = excludedSet.size
      ? fullTaxes.filter((tax) => !excludedSet.has(Number(tax.id)))
      : fullTaxes;
    const fullRate = sumTaxRate(fullTaxes) / 100;
    const remainingRate = sumTaxRate(appliedTaxes) / 100;

    (product?.Varieties || []).forEach((variety) => {
      (variety?.VarietyPrices || []).forEach((price, index) => {
        if (price?.price === null || price?.price === undefined) return;
        if (!allowPrice(price, variety, product)) return;
        const qty = normalizeQuantity(quantities?.[variety.id]?.[index] ?? 0);
        if (!qty || qty <= 0) return;
        const unitDiscount = toNumber(discounts?.[variety.id]?.[index] ?? 0);
        const unitPrice = toNumber(price.price) - unitDiscount;
        const baseGross = Math.floor(unitPrice * qty);
        let adjustedGross = baseGross;
        if (
          excludedSet.size
          && fullTaxes.length
          && appliedTaxes.length !== fullTaxes.length
          && fullRate > 0
        ) {
          const netFromFull = Math.round(baseGross / (1 + fullRate));
          adjustedGross = Math.round(netFromFull * (1 + remainingRate));
        }
        subtotal += adjustedGross;
      });
    });
  });

  const discountAmount = (() => {
    const percentToUse =
      typeof orderDiscountPercentage === 'number'
        ? orderDiscountPercentage
        : orderDiscountPercentage != null
          ? Number(orderDiscountPercentage)
          : null;
    if (typeof percentToUse === 'number') {
      return Math.floor(subtotal * (percentToUse / 100));
    }
    const amount =
      typeof orderDiscountAmount === 'number'
        ? orderDiscountAmount
        : orderDiscountAmount != null
          ? Number(orderDiscountAmount)
          : 0;
    return Math.min(Math.max(amount, 0), subtotal);
  })();

  const total = Math.max(subtotal - discountAmount, 0);
  return { subtotal, total };
};
